import React, { useContext } from 'react';
import { navigate, type RouteComponentProps } from '@gatsbyjs/reach-router';
import { useEffect, useState } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import { FORM_ERROR } from 'final-form';
import Catalog from '~/Catalog';
import { Auth } from '~/amplify';
import SetupUserDetailsForm from './components/SetupUserDetailsForm';
import useErrorReporter from '~/hooks/useErrorReporter';
import { Body, Heading1 } from '~/components/atom/Typography';
import AuthFormWrapper from '../components/AuthFormWrapper';
import createPageTitle from '~/util/createPageTitle';
import { AuthContext } from '~/Root/Auth';

export type FormData = {
  name: string | null;
  email: string | null;
  password: string | null;
  passwordRepeat: string | null;
  phone: string | null;
};

type Props = RouteComponentProps<{
  location?: RouteComponentProps['location'] & {
    state: {
      email?: string;
      phone?: string;
      name?: string;
    };
  };
}>;

const text = {
  genericErrorMessage: Catalog.genericUnknownErrorMessage,
  pageTitle: 'Vul je gegevens in',
};

type UserAttributes = {
  email: string;
  name?: string | null;
  phone?: string | null;
};

const SetupUserDetails: React.FC<Props> = ({ location }) => {
  const reporter = useErrorReporter();
  const { ensureLogin } = useContext(AuthContext);
  const [userState, setUserState] = useState<UserAttributes | null>(null);
  const [routeError, setRouteError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!location?.state || !location?.state.email) {
      setRouteError(true);
    } else {
      setUserState({
        email: location.state.email,
        phone: location.state.phone,
        name: location.state.name,
      });
    }
  }, [location?.state]);

  const onFormSubmit = async ({ name, password, phone }: FormData) => {
    if (!password) {
      return {
        [FORM_ERROR]: text.genericErrorMessage,
      };
    }

    setIsSubmitting(true);

    try {
      await Auth.confirmSignIn({
        challengeResponse: password,
        options: {
          userAttributes: {
            name: name || undefined,
            'custom:phone': phone || undefined,
          },
        },
      }).then(() => {
        ensureLogin();
        void navigate('/-/');
      });

      return;
    } catch (err) {
      reporter.captureException(err);
      return {
        [FORM_ERROR]: text.genericErrorMessage,
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasUnknownError = routeError;

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.pageTitle)}</title>
      </MetaTags>

      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={['l', null, 'xl', null]}
          color={{ group: 'accent' }}
          size="xxxxl"
          skewed
        >
          {text.pageTitle}
        </Heading1>

        {hasUnknownError ? (
          <Body
            size="base"
            color={{ group: 'danger', variant: 'light' }}
            margin={[null]}
            data-testid="setup-user-error-message"
          >
            {text.genericErrorMessage}
          </Body>
        ) : null}
        <SetupUserDetailsForm
          disabled={hasUnknownError}
          onFormSubmit={data =>
            onFormSubmit({
              ...data,
            })
          }
          initialValues={{
            name: userState?.name || null,
            password: null,
            passwordRepeat: null,
            phone: userState?.phone || null,
            email: userState?.email || null,
          }}
          loading={isSubmitting}
        />
      </AuthFormWrapper>
    </>
  );
};

export default SetupUserDetails;
